import {
    hasNewVersion,
    getNewVersionNotes,
    markCurrentVersionAsSeen,
    getCurrentAppVersion,
} from '@/services/releaseNotes'
import { ReleaseNote } from '@/types/releaseNotes'
import { formatDateWithFormatter } from '@/utils/function'
import { useTourLauncher } from '@/hooks/useTourLauncher'
import {
    Rocket,
    Sparkles,
    Bug,
    Wrench,
    Shield,
    ChevronLeft,
    ChevronRight,
    PartyPopper,
    PlayCircle,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
    Badge,
    Button,
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Heading,
    IconButton,
    Text,
    type BadgeTone,
} from '@vnetwork/datacentral-ui/primitives'
import type { ReactNode } from 'react'

const WHATS_NEW_DISMISSED_KEY = 'datacentral_whats_new_dismissed'

const CATEGORY: Record<string, { tone: BadgeTone; icon: ReactNode }> = {
    feature: { tone: 'success', icon: <Sparkles size={12} /> },
    bugfix: { tone: 'danger', icon: <Bug size={12} /> },
    improvement: { tone: 'info', icon: <Wrench size={12} /> },
    security: { tone: 'warning', icon: <Shield size={12} /> },
    default: { tone: 'primary', icon: <Rocket size={12} /> },
}

const WhatsNewModal = () => {
    const { t } = useTranslation('release-notes')
    const navigate = useNavigate()
    const { startFirstTourForVersion } = useTourLauncher({
        onComplete: () => {
            markCurrentVersionAsSeen()
        },
    })
    const [isOpen, setIsOpen] = useState(false)
    const [newNotes, setNewNotes] = useState<ReleaseNote[]>([])
    const [currentIndex, setCurrentIndex] = useState(0)

    useEffect(() => {
        // Check if there's a new version
        if (!hasNewVersion()) {
            return
        }

        // Check if modal was already dismissed in this session
        const currentVersion = getCurrentAppVersion()
        const dismissedVersion = sessionStorage.getItem(WHATS_NEW_DISMISSED_KEY)

        if (dismissedVersion === currentVersion) {
            return
        }

        // Get notes for new versions
        const notes = getNewVersionNotes()
        if (notes.length > 0) {
            setNewNotes(notes)
            // Delay to show after page load
            setTimeout(() => setIsOpen(true), 1000)
        }
    }, [])

    const handleDismiss = () => {
        const currentVersion = getCurrentAppVersion()
        sessionStorage.setItem(WHATS_NEW_DISMISSED_KEY, currentVersion)
        setIsOpen(false)
    }

    const handleMarkAsSeen = () => {
        markCurrentVersionAsSeen()
        setIsOpen(false)
    }

    const handleViewAll = () => {
        handleMarkAsSeen()
        navigate('/release-notes')
    }

    const handleStartTour = () => {
        setIsOpen(false)
        startFirstTourForVersion(currentNote.version)
    }

    if (newNotes.length === 0) return null

    const currentNote = newNotes[currentIndex]
    const hasMultiple = newNotes.length > 1
    const category = CATEGORY[currentNote.category] ?? CATEGORY.default

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(open) => (open ? setIsOpen(true) : handleDismiss())}
        >
            <DialogContent size="md">
                <DialogHeader>
                    <DialogTitle>
                        <span className="inline-flex items-center gap-2">
                            <PartyPopper size={18} />
                            {t('whats-new.title')}
                            <Badge size="sm" tone="primary">
                                v{currentNote.version}
                            </Badge>
                        </span>
                    </DialogTitle>
                    <DialogDescription>
                        {formatDateWithFormatter(
                            currentNote.date,
                            'DD/MM/YYYY'
                        )}
                    </DialogDescription>
                </DialogHeader>
                <DialogBody>
                    <div className="flex flex-col gap-2">
                        <Heading level={3} size="lg">
                            {currentNote.title}
                        </Heading>
                        <Badge
                            tone={category.tone}
                            startIcon={category.icon}
                            className="self-start"
                        >
                            {t(`category.${currentNote.category}`)}
                        </Badge>
                        <Text as="p" tone="muted">
                            {currentNote.summary}
                        </Text>
                        {hasMultiple && (
                            <Text as="p" size="sm" tone="subtle">
                                {t('whats-new.more-updates', {
                                    count: newNotes.length - 1,
                                })}
                            </Text>
                        )}
                    </div>
                </DialogBody>
                <DialogFooter>
                    {hasMultiple && (
                        <div className="mr-auto flex items-center gap-1">
                            <IconButton
                                aria-label="Previous"
                                size="sm"
                                disabled={currentIndex === 0}
                                onClick={() => setCurrentIndex((i) => i - 1)}
                            >
                                <ChevronLeft size={16} />
                            </IconButton>
                            <Text size="sm" tone="muted" numeric>
                                {currentIndex + 1} / {newNotes.length}
                            </Text>
                            <IconButton
                                aria-label="Next"
                                size="sm"
                                disabled={currentIndex === newNotes.length - 1}
                                onClick={() => setCurrentIndex((i) => i + 1)}
                            >
                                <ChevronRight size={16} />
                            </IconButton>
                        </div>
                    )}
                    <Button variant="secondary" onClick={handleDismiss}>
                        {t('whats-new.remind-later')}
                    </Button>
                    {currentNote.hasTour && (
                        <Button variant="ghost" onClick={handleViewAll}>
                            {t('whats-new.view-details')}
                        </Button>
                    )}
                    {currentNote.hasTour ? (
                        <Button
                            startIcon={<PlayCircle size={16} />}
                            onClick={handleStartTour}
                        >
                            {t('whats-new.start-tour')}
                        </Button>
                    ) : (
                        <Button
                            endIcon={<ChevronRight size={16} />}
                            onClick={handleViewAll}
                        >
                            {t('whats-new.view-details')}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export default WhatsNewModal
